import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import useDebounceValue from "@hooks/useDebounceValue";
import { useDispatch } from "react-redux";
import { hideFilterModal } from "@store/slices/filterModalSlice";
import { api } from "@config/axios/axios.config";
import { createCompleteApiParams } from "@utils/filterUtils";
import type { CommitteeHistoryFilters, CommitteHistory, CommitteeResponse, CommitteeData } from "../types";
import type { CommitteeDetailsResponse } from "../../MyCommittee/services/committeeDetails.service";
import { extractInspectionValue } from "@utils/dateFormat";
import { pickCommitteeRenewalFields } from "../../utils/committeeRenewalFields";

const transformCommitteeData = (data: CommitteeData[]): CommitteHistory[] => {
	return data.map(item => {
		const renewalFields = pickCommitteeRenewalFields(item as unknown as Record<string, unknown>);
		return {
		instituteId: item?.application?.instituteId || "N/A",
		appId: item?.application?.id?.toString() || "N/A",
		commitId: item?.id?.toString() || "N/A",
		instName: item?.application?.name || "N/A",
		inspectionType: item?.inspectionType,
		inspectionCount: item?.inspectionCount ?? 0,
		inspectDate: extractInspectionValue(item?.inspectionDate, "date") || "N/A",
		inspectTime: extractInspectionValue(item?.inspectionTime, "time") || "N/A",
		createdBy: item?.createdBy ? `${item.createdBy.firstName}` : "N/A",
		createdOn: extractInspectionValue(item?.createdAt, "date") || "N/A",
		verifiedBy: item?.verifiedBy ? `${item.verifiedBy?.firstName}` : "N/A",
		rejectedBy: item?.rejectedBy ? `${item.rejectedBy?.firstName}` : "N/A",
		verifiedOn: item?.approvedAt ? extractInspectionValue(item.approvedAt, "date") || "N/A" : "N/A",
		rejectedOn: item?.approvedAt ? extractInspectionValue(item.approvedAt, "date") || "N/A" : "N/A",
		editCount: item?.editCount || 0,
		activity: item?.application?.applicationType === "new" ? "New Registration" : "Renewal",
		session: item?.application?.session,
		...renewalFields,
	};
	});
};

// Generic hook for committee data (used for both approved and rejected)
const useCommitteeData = (queryKey: string, apiRoute: string, searchText?: string) => {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState<CommitteeHistoryFilters>({});
	const limit = 10;
	const [hasInitialData] = useState(false);
	const dispatch = useDispatch();

	const debouncedFilters = useDebounceValue(filters, 500);

	// Create API parameters with filters and search
	const apiParams = createCompleteApiParams(debouncedFilters, "committeesHistory", page, limit);

	// Add search parameter if provided
	if (searchText && searchText.trim()) {
		apiParams.search = searchText.trim();
	}

	const queryString = new URLSearchParams(apiParams).toString();

	const { data, isLoading, isFetching, isError, error, isSuccess } = useQuery<CommitteeResponse, Error>({
		queryKey: [queryKey, page, limit, debouncedFilters, searchText],
		queryFn: async (): Promise<CommitteeResponse> => {
			const baseUrl = `${apiRoute}`;
			const url = queryString ? `${baseUrl}${queryString}` : baseUrl;

			const response = await api.get<CommitteeResponse>(url);
			return response as unknown as CommitteeResponse;
		},
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		const hasActiveFilters = Object.values(debouncedFilters).some(value => {
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === "object" && value !== null) return Object.keys(value).length > 0;
			return value !== undefined && value !== null && value !== "";
		});
		if (hasActiveFilters) {
			setPage(1);
		}
	}, [debouncedFilters]);

	useEffect(() => {
		if (!isFetching && isSuccess) dispatch(hideFilterModal());
	}, [isSuccess, isFetching, dispatch]);

	// Transform the API data to match expected field names
	const transformedData = data?.data.data ? transformCommitteeData(data.data.data) : [];

	return {
		myInstitutes: transformedData,
		pagination: {
			total: data?.data?.pagination?.totalItems ?? 0,
			page: data?.data?.pagination?.currentPage ?? page,
			limit,
			totalPages: data?.data?.pagination?.totalPages ?? 0,
		},
		isLoading,
		isFetching,
		isError,
		error,
		page,
		setPage,
		filters,
		setFilters,
		hasInitialData,
	};
};

// Hook for approved committees
export function useApprovedCommittees(searchText?: string) {
	return useCommitteeData(
		QUERY_KEYS.INSTITUTES.COMMITTEE_APPROVED,
		SERVER_ROUTES.INSTITUTE.GET_COMMITTEE_APPROVED,
		searchText,
	);
}

// Hook for rejected committees
export function useRejectedCommittees(searchText?: string) {
	return useCommitteeData(
		QUERY_KEYS.INSTITUTES.COMMITTEE_REJECTED,
		SERVER_ROUTES.INSTITUTE.GET_COMMITTEE_REJECTED,
		searchText,
	);
}

// Hook for constituted (finalized) committees
export function useConstitutedCommittees(searchText?: string) {
	return useCommitteeData(
		QUERY_KEYS.INSTITUTES.COMMITTEE_FINALIZED,
		SERVER_ROUTES.INSTITUTE.GET_COMMITTEE_FINALIZED,
		searchText,
	);
}

// Hook for fetching committee details
export const useGetCommitteeDetails = (committeeId: string) => {
	return useQuery<CommitteeDetailsResponse, Error>({
		queryKey: [QUERY_KEYS.INSTITUTES.COMMITTEE_DETAILS, committeeId],
		queryFn: async (): Promise<CommitteeDetailsResponse> => {
			const response = await api.get<CommitteeDetailsResponse>(
				SERVER_ROUTES.INSTITUTE.GET_APPLICATION_COMMITTEE(committeeId),
			);
			return response.data;
		},
		enabled: !!committeeId,
	});
};
